/**
 * API Metadata Constraints
 *
 * Single source of truth for request schema validations derived from OpenAPI.
 * Used by validators, layer strategies, and metadata editors to match server enforcement.
 */

/** Default string maxLength for API request properties (OpenAPI pattern \S = non-whitespace). */
export const API_STRING_MAX_LENGTH = 255;

/** Pattern: at least one non-whitespace character (OpenAPI \S). */
export const API_STRING_PATTERN_NON_EMPTY = /\S/;

// =============================================================================
// SongMetadataRequest
// =============================================================================

export const SONG_METADATA = {
  requiredFields: ['genre', 'mood', 'songName', 'artistName', 'tempo'] as const,
  stringMaxLength: API_STRING_MAX_LENGTH,
  genre: { minItems: 1, maxItems: 50 },
  mood: { minItems: 1, maxItems: 50 },
} as const;

// =============================================================================
// AlgorhythmMetadataRequest
// =============================================================================

export const ALGORHYTHM_METADATA = {
  requiredArrayFields: [
    'genre',
    'mood',
    'targetAudience',
    'ageAppropriateness',
    'culturalContext',
    'musicalStyle',
    'performanceContext',
  ] as const,
  stringMaxLength: API_STRING_MAX_LENGTH,
  arrayMinItems: 1,
  arrayMaxItems: 50,
} as const;

/**
 * Check if a payload has all 7 API-required Algorhythm fields as non-empty arrays.
 * Used for button disable and "incomplete" warning. No defaults; values must be set.
 * Aligned with OpenAPI AlgorhythmMetadataRequest (no energyLevel).
 */
export function isAlgorhythmRequestComplete(payload: Record<string, unknown> | null | undefined): boolean {
  if (payload == null) return false;
  for (const key of ALGORHYTHM_METADATA.requiredArrayFields) {
    const value = payload[key];
    const arr = Array.isArray(value) ? value : typeof value === 'string' && value.trim() !== '' ? [value.trim()] : null;
    if (!arr || arr.length === 0) return false;
  }
  return true;
}

// =============================================================================
// LooksMetadataRequest
// =============================================================================

export const LOOKS_METADATA = {
  requiredFields: [
    'outfitName',
    'styleCategory',
    'style',
    'occasion',
    'colorScheme',
    'primaryGarment',
    'primaryColors',
    'formality',
  ] as const,
  stringMaxLength: API_STRING_MAX_LENGTH,
  primaryColors: { minItems: 1, maxItems: 10 },
  patterns: { maxItems: 10 },
  materials: { maxItems: 10 },
  accessories: { maxItems: 10 },
  brandNames: { maxItems: 10 },
} as const;

// =============================================================================
// MovesMetadataRequest
// =============================================================================

export const MOVES_METADATA = {
  requiredFields: ['tempo', 'moveName', 'danceStyle', 'difficulty'] as const,
  stringMaxLength: API_STRING_MAX_LENGTH,
  bodyParts: { maxItems: 10 },
  signatureMoves: { maxItems: 10 },
  techniquesRequired: { maxItems: 10 },
  propsNeeded: { maxItems: 10 },
} as const;

// =============================================================================
// StarMetadataRequest
// =============================================================================

export const STAR_METADATA = {
  requiredFields: [
    'starName',
    'gender',
    'ageGroup',
    'ethnicity',
    'mood',
  ] as const,
  stringMaxLength: API_STRING_MAX_LENGTH,
  musicalStyle: { maxItems: 10 },
  personalityTraits: { maxItems: 10 },
  performanceContext: { maxItems: 10 },
  targetAudience: { maxItems: 50 }, // Optional in current OpenAPI
  mood: { minItems: 1, maxItems: 3 },
} as const;

// =============================================================================
// WorldsMetadataRequest
// =============================================================================

export const WORLDS_METADATA = {
  requiredFields: ['worldName', 'locationType'] as const,
  stringMaxLength: API_STRING_MAX_LENGTH,
  dominantColors: { maxItems: 10 },
} as const;

// =============================================================================
// Layer required-fields completeness (OpenAPI *MetadataRequest)
// =============================================================================

function nonEmptyString(v: unknown): boolean {
  return typeof v === 'string' && v.trim() !== '';
}

function nonEmptyArray(v: unknown): boolean {
  return Array.isArray(v) && v.length > 0;
}

function arrayOrStringNonEmpty(v: unknown): boolean {
  if (Array.isArray(v)) return v.length > 0;
  return nonEmptyString(v);
}

/**
 * Returns true if the given metadata payload has all required fields for the layer (OpenAPI *MetadataRequest).
 * Used to enable/disable Save on the edit page. Payload should match what would be sent for that layer.
 */
export function isLayerRequiredFieldsComplete(
  layer: string,
  payload: Record<string, unknown> | null | undefined
): boolean {
  if (payload == null) return false;

  switch (layer) {
    case 'G': {
      for (const key of SONG_METADATA.requiredFields) {
        if (key === 'genre' || key === 'mood') {
          if (!arrayOrStringNonEmpty(payload[key])) return false;
        } else {
          if (!nonEmptyString(payload[key])) return false;
        }
      }
      return true;
    }
    case 'S': {
      for (const key of STAR_METADATA.requiredFields) {
        if (key === 'mood') {
          if (!arrayOrStringNonEmpty(payload[key])) return false;
        } else {
          if (!nonEmptyString(payload[key])) return false;
        }
      }
      return true;
    }
    case 'L': {
      for (const key of LOOKS_METADATA.requiredFields) {
        if (key === 'primaryColors') {
          if (!nonEmptyArray(payload[key])) return false;
        } else {
          if (!nonEmptyString(payload[key])) return false;
        }
      }
      return true;
    }
    case 'M': {
      for (const key of MOVES_METADATA.requiredFields) {
        const v = payload[key];
        if (typeof v === 'number') {
          if (Number.isNaN(v)) return false;
        } else if (!nonEmptyString(v)) {
          return false;
        }
      }
      return true;
    }
    case 'W': {
      for (const key of WORLDS_METADATA.requiredFields) {
        if (!nonEmptyString(payload[key])) return false;
      }
      return true;
    }
    default:
      return true;
  }
}

// =============================================================================
// Helpers
// =============================================================================

/**
 * Validates string max length (OpenAPI maxLength).
 * Returns error message or null if valid.
 */
export function validateStringMaxLength(
  value: string | undefined | null,
  maxLength: number = API_STRING_MAX_LENGTH,
  fieldName: string = 'Field'
): string | null {
  if (value == null || value === '') return null;
  const len = typeof value === 'string' ? value.length : 0;
  if (len > maxLength) {
    return `${fieldName} must be at most ${maxLength} characters`;
  }
  return null;
}

/**
 * Validates that string has at least one non-whitespace character (OpenAPI pattern \S).
 */
export function validateStringNonEmpty(
  value: string | undefined | null,
  fieldName: string = 'Field'
): string | null {
  if (value == null) return `${fieldName} is required`;
  if (typeof value !== 'string') return null;
  if (!API_STRING_PATTERN_NON_EMPTY.test(value.trim())) {
    return `${fieldName} cannot be empty or whitespace only`;
  }
  return null;
}

/**
 * Validates array length within min/max (OpenAPI minItems/maxItems).
 * Returns error message or null if valid.
 */
export function validateArrayLength(
  arr: unknown[] | undefined | null,
  options: { minItems?: number; maxItems?: number },
  fieldName: string = 'Field'
): string | null {
  if (arr == null) {
    if (options.minItems != null && options.minItems > 0) {
      return `${fieldName} is required (at least ${options.minItems} item(s))`;
    }
    return null;
  }
  if (!Array.isArray(arr)) return `${fieldName} must be an array`;
  const len = arr.length;
  if (options.minItems != null && len < options.minItems) {
    return `${fieldName} must have at least ${options.minItems} item(s)`;
  }
  if (options.maxItems != null && len > options.maxItems) {
    return `${fieldName} must have at most ${options.maxItems} item(s)`;
  }
  return null;
}

/**
 * Validates each array item is a non-empty string of max length (OpenAPI items maxLength 255, pattern \S).
 */
export function validateArrayItems(
  arr: unknown[] | undefined | null,
  maxItemLength: number = API_STRING_MAX_LENGTH,
  fieldName: string = 'Field'
): string | null {
  if (arr == null || !Array.isArray(arr)) return null;
  for (let i = 0; i < arr.length; i++) {
    const item = arr[i];
    if (item != null && typeof item === 'string') {
      const trimmed = item.trim();
      if (!API_STRING_PATTERN_NON_EMPTY.test(trimmed)) {
        return `${fieldName} item ${i + 1} cannot be empty or whitespace only`;
      }
      if (item.length > maxItemLength) {
        return `${fieldName} item ${i + 1} must be at most ${maxItemLength} characters`;
      }
    }
  }
  return null;
}

/** Array field limits per layer (key = field name, value = maxItems). Used for payload sanitization. */
const LAYER_ARRAY_LIMITS: Record<string, Record<string, number>> = {
  G: {
    genre: SONG_METADATA.genre.maxItems,
    mood: SONG_METADATA.mood.maxItems,
  },
  S: {
    musicalStyle: STAR_METADATA.musicalStyle.maxItems,
    personalityTraits: STAR_METADATA.personalityTraits.maxItems,
    performanceContext: STAR_METADATA.performanceContext.maxItems,
    targetAudience: STAR_METADATA.targetAudience.maxItems,
  },
  L: {
    primaryColors: LOOKS_METADATA.primaryColors.maxItems,
    patterns: LOOKS_METADATA.patterns.maxItems,
    materials: LOOKS_METADATA.materials.maxItems,
    accessories: LOOKS_METADATA.accessories.maxItems,
    brandNames: LOOKS_METADATA.brandNames.maxItems,
  },
  M: {
    bodyParts: MOVES_METADATA.bodyParts.maxItems,
    signatureMoves: MOVES_METADATA.signatureMoves.maxItems,
    signature_moves: MOVES_METADATA.signatureMoves.maxItems,
    techniquesRequired: MOVES_METADATA.techniquesRequired.maxItems,
    techniques_required: MOVES_METADATA.techniquesRequired.maxItems,
    propsNeeded: MOVES_METADATA.propsNeeded.maxItems,
    props_needed: MOVES_METADATA.propsNeeded.maxItems,
  },
  W: {
    dominantColors: WORLDS_METADATA.dominantColors.maxItems,
    dominant_colors: WORLDS_METADATA.dominantColors.maxItems,
  },
};

/**
 * Sanitize metadata for API: trim strings to maxLength 255 and slice arrays to maxItems.
 * Call before sending payload so requests comply even if UI lags.
 */
export function sanitizeMetadataForApi(
  metadata: any,
  layer: 'G' | 'S' | 'L' | 'M' | 'W'
): any {
  if (metadata == null) return metadata;
  const limits = LAYER_ARRAY_LIMITS[layer] || {};
  const out: any = {};
  for (const key of Object.keys(metadata)) {
    const value = metadata[key];
    if (typeof value === 'string') {
      out[key] = value.length > API_STRING_MAX_LENGTH ? value.slice(0, API_STRING_MAX_LENGTH) : value;
    } else if (Array.isArray(value)) {
      const max = limits[key] ?? 50;
      const arr = value.slice(0, max);
      out[key] = arr.map((item: any) =>
        typeof item === 'string' && item.length > API_STRING_MAX_LENGTH
          ? item.slice(0, API_STRING_MAX_LENGTH)
          : item
      );
    } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      if (key === 'algorhythmMetadata') {
        out[key] = sanitizeAlgorhythmMetadataForApi(value);
      } else {
        out[key] = sanitizeMetadataForApi(value, layer);
      }
    } else {
      out[key] = value;
    }
  }
  return out;
}

function sanitizeAlgorhythmMetadataForApi(metadata: any): any {
  if (metadata == null) return metadata;
  const out: any = {};
  const maxArr = ALGORHYTHM_METADATA.arrayMaxItems;
  for (const key of Object.keys(metadata)) {
    const value = metadata[key];
    if (typeof value === 'string') {
      out[key] = value.length > API_STRING_MAX_LENGTH ? value.slice(0, API_STRING_MAX_LENGTH) : value;
    } else if (Array.isArray(value)) {
      const arr = value.slice(0, maxArr);
      out[key] = arr.map((item: any) =>
        typeof item === 'string' && item.length > API_STRING_MAX_LENGTH
          ? item.slice(0, API_STRING_MAX_LENGTH)
          : item
      );
    } else {
      out[key] = value;
    }
  }
  return out;
}
